import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  withStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import ForecastTrendChart from './ForecastTrendChart';
import { CumulativeClosedWonChart, PipelineByStageChart, ForecastAccuracyChart } from './DealProgressionCharts';
import { PipelineAgingChart, ConversionCycletimeChart, QualificationConversionChart, POCConversionChart } from './DealConversionCharts';
import UpsellCrossSellChart from './UpsellCrossSellChart';
import QualifiedPipelineCoverageChart from '../PipelineReviewPackage/QualifiedPipelineCoverageChart';
import SalesForecastCurrentQuarterTable from './SalesForecastCurrentQuarterTable';
import { countOrACVFormatterMillionFixedOne, dateFormatterMomentV3 } from '../../util/customFunctions';
import * as d3 from 'd3';
import { updatedScheme10 } from '../../util/chartColorScheme';
import RegionalSalesReviewPackagePPTX from './RegionalSalesReviewPackagePPTX';
import { ResizableTable } from '../../components/resizableTable/ResizableTable';
import AddHeaderDescriptionWithHelperText from '../../components/HelperTextDescription';
import SingleLevelDrilldown from '../../components/Drilldown/SingleLevelDrilldown';
import DrilldownWithSLD from '../../components/Drilldown/DrilldownWithSLD';
import ExpectedAcvPage from '../NewWaterFall/ExpectedAcvPage';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1.2rem',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 250,
    width: '100%',
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendBox: {
    width: 16,
    height: 16,
    borderRadius: 2,
  },
  statusBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  elapsedBadge: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
  currentBadge: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  futureBadge: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  summaryCard: {
    backgroundColor: '#fff',
    marginTop: theme.spacing(2),
  },
  summaryItem: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1976d2',
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: '#666',
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  currentQuarterInfo: {
    marginTop: theme.spacing(1),
  },
  // Paper 2 Table Styles
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: '#1976d2',
    '& th': {
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.875rem',
      padding: '12px 8px',
      borderRight: '1px solid rgba(255,255,255,0.2)',
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  newOpportunityRow: {
    backgroundColor: '#c8e6c9 !important',
    '&:hover': {
      backgroundColor: '#a5d6a7 !important',
    },
  },
  tableCell: {
    fontSize: '0.813rem',
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  summaryRow: {
    backgroundColor: '#f5f5f5',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  targetRow: {
    backgroundColor: '#fff3cd',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  notesBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fffbea',
    borderRadius: 4,
    border: '1px solid #ffd54f',
  },
  noteItem: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  leftNote: {
    backgroundColor: '#fff9e6',
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: '0.75rem',
    lineHeight: 1.6,
  },
  filterWrapper: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'inline-flex',
    width: 'auto',
  },
  formControl: {
    minWidth: 200,
  },
  teamFilterFormControl: {
    minWidth: 200,
  },
  goButton: {
    borderRadius: 4,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
  },
  filterLabel: {
    color: '#000',
  },
  filterLabelActive: {
    color: '#4472c4',
  },
});

const stickyHeaderStyle = {
  backgroundColor: '#1976d2',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '12px 8px',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

// Helper function to calculate column width based on text length
// Caps at 80 characters - longer text will wrap instead of expanding column
const calculateColumnWidth = (textLength) => {
  // Cap text length at 80 characters for width calculation
  const cappedLength = Math.min(textLength, 80);
  
  if (cappedLength >= 0 && cappedLength <= 2) {
    return 48;
  } else if (cappedLength >= 3 && cappedLength <= 5) {
    return 60;
  } else if (cappedLength >= 6 && cappedLength <= 10) {
    return 80;
  } else if (cappedLength >= 11 && cappedLength <= 20) {
    return 120;
  } else if (cappedLength >= 21 && cappedLength <= 30) {
    return 180;
  } else if (cappedLength >= 31 && cappedLength <= 45) {
    return 240;
  } else if (cappedLength >= 46 && cappedLength <= 60) {
    return 352;
  } else if (cappedLength > 60 && cappedLength <= 80) {
    return 500; // Width for ~80 characters (text will wrap if longer)
  }
  return 120; // default
};

// Helper function to calculate default width for a column based on row data
const calculateDefaultWidthForField = (field, rows, getCellValue) => {
  let maxLength = field.length; // Start with header length
  
  if (rows && rows.length > 0) {
    // Sample up to 20 rows to calculate max length
    const sampleRows = rows.slice(0, 20);
    sampleRows.forEach((row) => {
      let value = row[field];
      
      // Use getCellValue if provided to get formatted value
      if (getCellValue) {
        const formattedValue = getCellValue(row, field);
        value = formattedValue != null ? String(formattedValue) : '';
      } else {
        value = value != null ? String(value) : '';
      }
      
      const stringValue = String(value || '').trim();
      // Cap the length at 80 characters for width calculation
      maxLength = Math.max(maxLength, Math.min(stringValue.length, 40));
    });
  }
  
  // Cap final length at 80 characters
  maxLength = Math.min(maxLength, 40);
  
  return calculateColumnWidth(maxLength);
};

// Helper function to create columns for opportunity tables with dynamic width calculation
const createOpportunityTableColumns = (rows = [], getCellValue = null) => {
  // Calculate widths for each field based on actual data
  const rowNumWidth = 40; // Fixed width for row number
  const opportunityWidth = calculateDefaultWidthForField('opportunity', rows, getCellValue);
  const ownerWidth = calculateDefaultWidthForField('owner', rows, getCellValue);
  const acvInKWidth = calculateDefaultWidthForField('acvInK', rows, getCellValue);
  const discountPercentWidth = 40; // Fixed width for percentage
  const projectedCloseDateWidth = calculateDefaultWidthForField('projectedCloseDate', rows, getCellValue);
  const ageSinceQualDaysWidth = calculateDefaultWidthForField('ageSinceQualDays', rows, getCellValue);
  const stageCharWidth = 50;
  const daysInStageWidth = 50;
  const partnerWidth = calculateDefaultWidthForField('partner', rows, getCellValue);
  const forecastCategoryWidth = calculateDefaultWidthForField('forecastCategory', rows, getCellValue);
  const nextStepWidth = calculateDefaultWidthForField('nextStep', rows, getCellValue);
  
  return [
  {
    label: '#',
    sortable: false,
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'center',
      width: 20,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Opportunity',
    sortable: true,
    subColumns: [{
      label: 'Opportunity',
      field: 'opportunity',
      type: 'text',
      align: 'left',
      width: 115,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Owner',
    sortable: true,
    subColumns: [{
      label: 'Owner',
      field: 'owner',
      type: 'text',
      align: 'left',
      width: 45,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
   {
    label: 'Lead Source',
    sortable: true,
    subColumns: [{
      label: 'Lead Source',
      field: 'Lead_Source',
      type: 'text',
      align: 'left',
      width: 37,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'ACV ($K)',
    sortable: true,
    subColumns: [{
      label: 'ACV ($K)',
      field: 'acvInK',
      type: 'number',
      align: 'right',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Disc. %',
    sortable: true,
    subColumns: [{
      label: 'Disc. %',
      field: 'discountPercent',
      type: 'percentage',
      align: 'right',
      width: 20,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Proj. Close Date',
    sortable: true,
    subColumns: [{
      label: 'Proj. Close Date',
      field: 'projectedCloseDate',
      type: 'date',
      align: 'right',
      width: 44,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Age since Qual. (Days)',
    sortable: true,
    subColumns: [{
      label: 'Age since Qual. (Days)',
      field: 'ageSinceQualDays',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Stage',
    sortable: true,
    subColumns: [{
      label: 'Stage',
      field: 'stageChar',
      type: 'text',
      align: 'center',
      width: 25 ,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Days in Stg.',
    sortable: true,
    subColumns: [{
      label: 'Days in Stg.',
      field: 'daysInStage',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Forecast Category',
    sortable: true,
    subColumns: [{
      label: 'Forecast Category',
      field: 'forecastCategory',
      type: 'text',
      align: 'left',
      width: forecastCategoryWidth,
      width: 50,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Partner',
    sortable: true,
    subColumns: [{
      label: 'Partner',
      field: 'partner',
      type: 'text',
      align: 'left',
      width: 70,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Risks',
    sortable: false,
    subColumns: [{
      label: 'Risks',
      field: 'risks',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Actions',
    sortable: false,
    subColumns: [{
      label: 'Actions',
      field: 'actions',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  }
];
};

// Helper function to transform opportunity data to rows
const transformOpportunityDataToRows = (opps, includeTotal = false, totalData = null) => {
  const rows = opps?.map(opp => ({
    rowNum: opp.rowNum,
    opportunity: opp.opportunity,
    owner: opp.owner,
    acvInK: opp.acvInK,
    discountPercent: null, // TODO: Add when available
    projectedCloseDate: opp.projectedCloseDate,
    ageSinceQualDays: opp.ageSinceQualDays,
    stageChar: opp.stageChar,
    daysInStage: opp.daysInStage != null ? opp.daysInStage : null,
    partner: opp.partner,
    Lead_Source: opp.Lead_Source || '',
    forecastCategory: opp.forecastCategory,
    nextStep: opp.nextStep,
    isNewThisWeek: opp.isNewThisWeek,
    risks: opp.risks || '',
    actions: opp.actions || '',
    oppId: opp.oppId || opp.Opp_ID || null, // Preserve oppId for drilldown
    Opp_ID: opp.Opp_ID || opp.oppId || null, // Also preserve Opp_ID for drilldown
    label: opp.opportunity, // For rowKey
  })) || [];

  if (includeTotal && totalData) {
    rows.push({
      rowNum: null,
      opportunity: `Total (${totalData.totalCount} opps)`,
      owner: null,
      acvInK: totalData.totalACVInK,
      discountPercent: null,
      projectedCloseDate: null,
      ageSinceQualDays: null,
      stageChar: null,
      daysInStage: null,
      partner: null,
      forecastCategory: null,
      nextStep: null,
      isNewThisWeek: false,
      label: 'Total',
    });
  }

  return rows;
};

// Helper function to create Win-Loss table columns
const createWinLossTableColumns = () => [
  {
    label: 'Customer Name',
    sortable: true,
    subColumns: [{
      label: 'Customer Name',
      field: 'customerName',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'ACV ($M)',
    sortable: true,
    subColumns: [{
      label: 'ACV ($M)',
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 100,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Competition',
    sortable: true,
    subColumns: [{
      label: 'Competition',
      field: 'competition',
      type: 'text',
      align: 'left',
      width: 150,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Key Learnings',
    sortable: false,
    subColumns: [{
      label: 'Key Learnings',
      field: 'keyLearnings',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Helper function to create Upsell-Cross Sell tracker detail table columns
const createUpsellCrossSellDetailColumns = (months) => {
  const columns = [
    {
      label: '',
      sortable: false,
      subColumns: [{
        label: '',
        field: 'rowLabel',
        type: 'text',
        align: 'center',
        width: 100,
        sortable: false,
      }],
      headerBgColor: '#f5f5f5',
      headerTextColor: '#000000',
    },
  ];

  months.forEach((month, monthIdx) => {
    columns.push({
      label: month,
      sortable: false,
      subColumns: [
        {
          label: 'Customer',
          field: `${month}_customer`,
          type: 'text',
          align: 'center',
          width: 120,
          sortable: true,
          filterable: true,
        },
        {
          label: 'Target',
          field: `${month}_target`,
          type: 'number',
          align: 'center',
          width: 100,
          sortable: true,
          filterable: true,
        },
        {
          label: 'Actual',
          field: `${month}_actual`,
          type: 'number',
          align: 'center',
          width: 100,
          sortable: true,
          filterable: true,
        },
      ],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    });
  });

  return columns;
};

// Helper function to create summary table columns
const createSummaryTableColumns = () => [
  {
    label: '',
    sortable: false,
    subColumns: [{
      label: '',
      field: 'label',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: '# of Opps',
    sortable: false,
    subColumns: [{
      label: '# of Opps',
      field: 'count',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'ACV ($M)',
    sortable: false,
    subColumns: [{
      label: 'ACV ($M)',
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Helper function to create Renewals table columns
const createRenewalsTableColumns = () => [
  {
    label: '#',
    sortable: false,
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'center',
      width: 50,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Customer Name',
    sortable: true,
    subColumns: [{
      label: 'Customer Name',
      field: 'customerName',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Renewal Date',
    sortable: true,
    subColumns: [{
      label: 'Renewal Date',
      field: 'renewalDate',
      type: 'text',
      align: 'center',
      width: 80,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Contract Value ($M)',
    sortable: true,
    subColumns: [{
      label: 'Contract Value ($M)',
      field: 'contractValue',
      type: 'number',
      align: 'right',
      width: 180,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Probability (%)',
    sortable: true,
    subColumns: [{
      label: 'Probability (%)',
      field: 'probability',
      type: 'number',
      align: 'center',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Status',
    sortable: true,
    subColumns: [{
      label: 'Status',
      field: 'status',
      type: 'text',
      align: 'center',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Risk Factors',
    sortable: false,
    subColumns: [{
      label: 'Risk Factors',
      field: 'riskFactors',
      type: 'text',
      align: 'left',
      width: 200,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Action Items',
    sortable: false,
    subColumns: [{
      label: 'Action Items',
      field: 'actionItems',
      type: 'text',
      align: 'left',
      width: 200,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

const RegionalSalesReviewPackage = (props) => {
  const { classes, forecastTrendData, salesForecastCurrentQuarterData, forecastMovedOutData, salesForecastNextQuarterData, salesForecastNextQuarterPlus1Data, dealProgressionData, dealConversionData, upsellCrossSellData, qualifiedPipelineCoverageData, rsrFirstLevelTeams, rsrSelectedTeam, rsrDefaultTeam } = props;

  // Local state for pending team selection (before clicking Go)
  const [pendingTeam, setPendingTeam] = useState('All');
  // Applied team filter (after clicking Go)
  const [appliedTeam, setAppliedTeam] = useState('All');
  // Track if initial data has been loaded
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Drilldown state
  const [secondLevelLocation, setSecondLevelLocation] = useState('');
  // Forecast Trend drilldown state
  const [forecastTrendDrilldownLocation, setForecastTrendDrilldownLocation] = useState('');
  const [forecastTrendDrilldownFilters, setForecastTrendDrilldownFilters] = useState([]);
  // Qualified Pipeline Coverage drilldown state
  const [qualifiedPipelineDrilldownOpen, setQualifiedPipelineDrilldownOpen] = useState(false);
  const [qualifiedPipelineDrilldownFilters, setQualifiedPipelineDrilldownFilters] = useState([]);

  // Reset to 'All' when component mounts
  useEffect(() => {
    setPendingTeam('All');
    setAppliedTeam('All');
    setInitialLoadDone(false);
  }, []);

  // Fetch first-level teams on mount
  useEffect(() => {
    props.getFirstLevelTeams();
  }, []);

  // Set default team when teams API returns (uses manager's team if applicable)
  useEffect(() => {
    if (!initialLoadDone && rsrFirstLevelTeams && rsrFirstLevelTeams.length > 0) {
      const defaultTeam = rsrDefaultTeam || 'All';
      setPendingTeam(defaultTeam);
      setAppliedTeam(defaultTeam);
      const filters = defaultTeam !== 'All' ? [{ name: 'team', value: defaultTeam }] : [];
      props.getForecastTrendData(filters);
      props.getSalesForecastCurrentQuarterData(filters);
      props.getForecastMovedOutData(filters);
      props.getSalesForecastNextQuarterData(filters);
      props.getSalesForecastNextQuarterPlus1Data(filters);
      props.getDealProgressionData(filters);
      props.getDealConversionData(filters);
      props.getUpsellCrossSellData(filters);
      props.getQualifiedPipelineCoverageData(filters);
      setInitialLoadDone(true);
    }
  }, [rsrFirstLevelTeams, rsrDefaultTeam, initialLoadDone]);

  // Handle team filter change (just updates pending, doesn't fetch)
  const handleTeamChange = (event) => {
    setPendingTeam(event.target.value);
  };

  // Handle Go button click - apply the filter and fetch data
  const handleGo = () => {
    setAppliedTeam(pendingTeam);
    const filters = pendingTeam !== 'All' ? [{ name: 'team', value: pendingTeam }] : [];
    props.getForecastTrendData(filters);
    props.getSalesForecastCurrentQuarterData(filters);
    props.getForecastMovedOutData(filters);
    props.getSalesForecastNextQuarterData(filters);
    props.getSalesForecastNextQuarterPlus1Data(filters);
    props.getDealProgressionData(filters);
    props.getDealConversionData(filters);
    props.getUpsellCrossSellData(filters);
    props.getQualifiedPipelineCoverageData(filters);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'elapsed':
        return classes.elapsedBadge;
      case 'current':
        return classes.currentBadge;
      default:
        return classes.futureBadge;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'elapsed':
        return 'Completed';
      case 'current':
        return 'Current';
      default:
        return 'Future';
    }
  };

  // Drilldown handlers - Directly go to second-level drilldown with oppId
  const handleChangeLocation = (location, row, tableType) => {
    if (location === "drilldownOnGraph") {
      // Don't allow drilldown on Total, Quota, or Other Opps rows
      if (row.label === 'Total' || row.label === 'Quota' || row.label === 'Other Opps') {
        return;
      }

      // Get oppId from row and directly show second-level drilldown (opportunity change events)
      const oppId = row.Opp_ID || row.oppId;
      if (oppId) {
        handleSecondLevelDrilldown("drilldownOppId", oppId);
      } else {
        // If no oppId is available, log a warning (could also show a user-friendly message)
        console.warn('No oppId found in row data for drilldown:', row);
      }
    } else {
      setSecondLevelLocation('');
      props.clearExpectedAcvData();
    }
  };

  const handleChangeLocationBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  const handleSecondLevelDrilldown = (location, oppId) => {
    setSecondLevelLocation(location);
    props.getOpenOppData(oppId);
  };

  // Handler for ForecastTrend chart bar click
  const handleForecastTrendDrilldown = ({ type, quarter, weekEnding }) => {
    const filters = [
      { name: 'quarter', value: quarter },
      { name: 'weekEnding', value: weekEnding },
    ];
    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }

    setForecastTrendDrilldownLocation(type);

    // Build display filters - include team if selected
    const displayFilters = [
      { title: 'Quarter', value: quarter },
      { title: 'Week Ending', value: weekEnding },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    setForecastTrendDrilldownFilters(displayFilters);

    if (type === 'won') {
      props.getForecastTrendWonDrilldown(filters);
    } else if (type === 'mostLikely') {
      props.getForecastTrendMostLikelyDrilldown(filters);
    }
  };

  // Handler to close forecast trend drilldown
  const handleForecastTrendDrilldownBack = () => {
    setForecastTrendDrilldownLocation('');
    setForecastTrendDrilldownFilters([]);
    props.clearForecastTrendDrilldown();
  };

  // Step back from second level to first level drilldown (for forecast trend)
  const handleForecastTrendStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  // Handler for qualified pipeline coverage drilldown
  const handleQualifiedPipelineDrilldown = ({ quarter }) => {
    const filters = [
      { name: 'quarter', value: quarter },
    ];
    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }

    setQualifiedPipelineDrilldownOpen(true);

    // Build display filters
    const displayFilters = [
      { title: 'Quarter', value: quarter },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    setQualifiedPipelineDrilldownFilters(displayFilters);

    props.getQualifiedPipelineCoverageDrilldown(filters);
  };

  // Handler to close qualified pipeline coverage drilldown
  const handleQualifiedPipelineDrilldownBack = () => {
    setQualifiedPipelineDrilldownOpen(false);
    setQualifiedPipelineDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearQualifiedPipelineCoverageDrilldown();
    props.clearExpectedAcvData();
  };

  // Step back from second level to first level drilldown (for qualified pipeline)
  const handleQualifiedPipelineStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  const isForecastTrendLoading = forecastTrendData === "";
  const isSalesForecastCurrentQuarterLoading = salesForecastCurrentQuarterData === "";
  const isForecastMovedOutLoading = forecastMovedOutData === "";
  const isSalesForecastNextQuarterLoading = salesForecastNextQuarterData === "";
  const isQualifiedPipelineCoverageLoading = qualifiedPipelineCoverageData === "";
  const isSalesForecastNextQuarterPlus1Loading = salesForecastNextQuarterPlus1Data === "";
  const isDealProgressionLoading = dealProgressionData === "";
  const isDealConversionLoading = dealConversionData === "";
  const isUpsellCrossSellLoading = upsellCrossSellData === "";

  // Format "As of MMM. DD, YYYY" from a date string (e.g., "2026-01-24")
  const formatAsOfDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `As of ${monthNames[date.getMonth()]}. ${date.getDate()}, ${date.getFullYear()}`;
  };

  const asOfDateLabel = formatAsOfDate(forecastTrendData?.mostRecentWeekEnding);
  // Helper function to format date to MM/DD/YYYY using existing customFunctions
  const formatDateToMMDDYYYY = useCallback((dateString) => {
    if (!dateString || dateString === '') return '';
    try {
      // Use dateFormatterMomentV3 which returns MM/DD/YYYY
      return dateFormatterMomentV3(dateString);
    } catch (error) {
      return '';
    }
  }, []);

  // Helper function to format cell values
  const getCellValue = useCallback((row, field, rowIndex) => {
    // Always show sequential row numbers regardless of sort order
    if (field === 'rowNum') {
      // For Total/Quota/Other Opps rows, return null or original value
      if (row.label === 'Total' || row.label === 'Quota' || row.label === 'Other Opps') {
        return row[field] != null ? row[field] : null;
      }
      // For regular rows, use rowIndex to show sequential numbers
      return rowIndex != null ? rowIndex + 1 : (row[field] != null ? row[field] : '');
    }
    if (field === 'projectedCloseDate') {
      return formatDateToMMDDYYYY(row[field]);
    }
    if (field === 'acvInK' && row[field] != null) {
      return d3.format(",.1f")(row[field]);
    }
    if (field === 'ageSinceQualDays' && row[field] != null) {
      return d3.format(",")(row[field]);
    }
    if (field === 'daysInStage' && row[field] != null) {
      return d3.format(",")(row[field]);
    }
    if (row[field] == null || row[field] === '') {
      return '';
    }
    return row[field];
  }, [formatDateToMMDDYYYY]);

  // Helper function to get cell styles
  const getCellStyle = useCallback((row, field) => {
    if (row.label === 'Total') {
      return { fontWeight: 600, backgroundColor: '#f5f5f5' };
    }
    if (row.isNewThisWeek) {
      return { backgroundColor: '#c8e6c9' };
    }
    return {};
  }, []);

  // Prepare data for Forecast Moved Out table
  const forecastMovedOutRows = useMemo(() => {
    const rows = transformOpportunityDataToRows(
      forecastMovedOutData?.movedOutOpps,
      forecastMovedOutData?.movedOutOpps?.length > 0,
      forecastMovedOutData?.movedOutOpps?.length > 0 ? {
        totalCount: forecastMovedOutData.totalCount,
        totalACVInK: forecastMovedOutData.totalACVInK
      } : null
    );
    return rows;
  }, [forecastMovedOutData]);

  // Prepare columns for Forecast Moved Out table (after rows are calculated)
  const forecastMovedOutColumns = useMemo(() => 
    createOpportunityTableColumns(forecastMovedOutRows, getCellValue), 
    [forecastMovedOutRows, getCellValue]
  );

  // Prepare data for Sales Forecast Next Quarter table
  const salesForecastNextQuarterRows = useMemo(() => {
    const opps = salesForecastNextQuarterData?.forecastOpps?.slice(0, 15) || [];
    const rows = transformOpportunityDataToRows(opps);
    
    // Add "Other Opps" row if there are more than 15
    if (salesForecastNextQuarterData?.forecastOpps?.length > 15) {
      const otherOpps = salesForecastNextQuarterData.forecastOpps.slice(15);
      const otherOppsTotal = otherOpps.reduce((sum, opp) => sum + parseFloat(opp.acvInK || 0), 0);
      rows.push({
        rowNum: null,
        opportunity: `Other Opps (${otherOpps.length})`,
        owner: null,
        acvInK: otherOppsTotal,
        discountPercent: null,
        projectedCloseDate: null,
        ageSinceQualDays: null,
        stageChar: null,
        daysInStage: null,
        partner: null,
        forecastCategory: null,
        nextStep: null,
        isNewThisWeek: false,
        label: 'Other Opps',
      });
    }
    
    // Add Total row
    if (salesForecastNextQuarterData?.forecastOpps?.length > 0) {
      rows.push({
        rowNum: null,
        opportunity: `Total (${salesForecastNextQuarterData.totalCount} opps)`,
        owner: null,
        acvInK: salesForecastNextQuarterData.totalACVInK,
        discountPercent: null,
        projectedCloseDate: null,
        ageSinceQualDays: null,
        stageChar: null,
        daysInStage: null,
        partner: null,
        forecastCategory: null,
        nextStep: null,
        isNewThisWeek: false,
        label: 'Total',
      });
    }
    
    // Add Quota row if available
    if (salesForecastNextQuarterData?.quota > 0) {
      rows.push({
        rowNum: null,
        opportunity: 'Quota',
        owner: null,
        acvInK: salesForecastNextQuarterData.quota / 1000,
        discountPercent: null,
        projectedCloseDate: null,
        ageSinceQualDays: null,
        stageChar: null,
        daysInStage: null,
        partner: null,
        forecastCategory: null,
        nextStep: null,
        isNewThisWeek: false,
        label: 'Quota',
      });
    }
    
    return rows;
  }, [salesForecastNextQuarterData]);

  // Prepare columns for Sales Forecast Next Quarter table (after rows are calculated)
  const salesForecastNextQuarterColumns = useMemo(() => 
    createOpportunityTableColumns(salesForecastNextQuarterRows, getCellValue), 
    [salesForecastNextQuarterRows, getCellValue]
  );

  // Prepare data for Sales Forecast Next Quarter +1 table
  const salesForecastNextQuarterPlus1Rows = useMemo(() => {
    const opps = salesForecastNextQuarterPlus1Data?.forecastOpps?.slice(0, 15) || [];
    const rows = transformOpportunityDataToRows(opps);
    
    // Add "Other Opps" row if there are more than 15
    if (salesForecastNextQuarterPlus1Data?.forecastOpps?.length > 15) {
      const otherOpps = salesForecastNextQuarterPlus1Data.forecastOpps.slice(15);
      const otherOppsTotal = otherOpps.reduce((sum, opp) => sum + parseFloat(opp.acvInK || 0), 0);
      rows.push({
        rowNum: null,
        opportunity: `Other Opps (${otherOpps.length})`,
        owner: null,
        acvInK: otherOppsTotal,
        discountPercent: null,
        projectedCloseDate: null,
        ageSinceQualDays: null,
        stageChar: null,
        daysInStage: null,
        partner: null,
        forecastCategory: null,
        nextStep: null,
        isNewThisWeek: false,
        label: 'Other Opps',
      });
    }
    
    // Add Total row
    if (salesForecastNextQuarterPlus1Data?.forecastOpps?.length > 0) {
      rows.push({
        rowNum: null,
        opportunity: `Total (${salesForecastNextQuarterPlus1Data.totalCount} opps)`,
        owner: null,
        acvInK: salesForecastNextQuarterPlus1Data.totalACVInK,
        discountPercent: null,
        projectedCloseDate: null,
        ageSinceQualDays: null,
        stageChar: null,
        daysInStage: null,
        partner: null,
        forecastCategory: null,
        nextStep: null,
        isNewThisWeek: false,
        label: 'Total',
      });
    }
    
    // Add Quota row if available
    if (salesForecastNextQuarterPlus1Data?.quota > 0) {
      rows.push({
        rowNum: null,
        opportunity: 'Quota',
        owner: null,
        acvInK: salesForecastNextQuarterPlus1Data.quota / 1000,
        discountPercent: null,
        projectedCloseDate: null,
        ageSinceQualDays: null,
        stageChar: null,
        daysInStage: null,
        partner: null,
        forecastCategory: null,
        nextStep: null,
        isNewThisWeek: false,
        label: 'Quota',
      });
    }
    
    return rows;
  }, [salesForecastNextQuarterPlus1Data]);

  // Prepare columns for Sales Forecast Next Quarter +1 table (after rows are calculated)
  const salesForecastNextQuarterPlus1Columns = useMemo(() => 
    createOpportunityTableColumns(salesForecastNextQuarterPlus1Rows, getCellValue), 
    [salesForecastNextQuarterPlus1Rows, getCellValue]
  );

  return (
    <div className={classes.root}>
      <AddHeaderDescriptionWithHelperText
        header={`Regional Sales Review Package`}
        // description={''}
        description={`Regional Sales Review Package`}
        helperTextCharts={props.helperTextCharts}
      />
      {/* Team Filter */}
      <Box className={classes.filterWrapper}>
        <Paper className={classes.filterContainer} elevation={0}>
          <FormControl className={classes.formControl}>
            <InputLabel
              className={classes.filterLabel}
              id="team-filter-label"
              style={{
                color: pendingTeam !== '' && pendingTeam !== 'All' ? '#4472c4' : '#000',
              }}
            >
              Team:
            </InputLabel>
            <Select
              labelId="team-filter-label"
              id="team-filter"
              value={pendingTeam}
              onChange={handleTeamChange}
              label="Team:"
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {rsrFirstLevelTeams && rsrFirstLevelTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        <Button
          color="primary"
          variant="contained"
          onClick={handleGo}
          className={classes.goButton}
        >
          GO
        </Button>
      </Box>

      {/* PPTX Export Button */}
      <RegionalSalesReviewPackagePPTX
        dashboardTitle={`Regional Sales Review Package`}
        fileName={`Regional_Sales_Review_Package`}
        currentQuarter={salesForecastCurrentQuarterData?.currentQuarter}
        currentWeekNumber={salesForecastCurrentQuarterData?.currentWeekNumber}
      />

      {/* Paper 1: Forecast Trend Charts */}
      <Paper className={classes.paper}>
        {/* <div className={classes.header}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <div style={{ flex: 1 }} />
            <Box display="flex" flexDirection='column' alignItems="center" flex={2}>
              <Typography variant="h5" className={classes.title}>
                {forecastTrendData?.title || 'Forecast Trend'} ($M)
              </Typography>
              <span className={classes.updateFrequency}>
                {"Live Data"}
              </span>
            </Box>
            <Box display="flex" justifyContent="flex-end" flex={1}>
              <RegionalSalesReviewPackagePPTX
                dashboardTitle={`Regional Sales Review Package`}
                fileName={`Regional_Sales_Review_Package`}
                currentQuarter={salesForecastCurrentQuarterData?.currentQuarter}
                currentWeekNumber={salesForecastCurrentQuarterData?.currentWeekNumber}
                inline={true}
              />
            </Box>
          </Box>
        </div> */}
         <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {forecastTrendData?.title || 'Forecast Trend'} ($M)
            </Typography>
            <span className={classes.updateFrequency}>
              {"Live Data"}
            </span>
          </Box>
        </div>

        {/* Quarterly Charts - 2x2 Grid with Loading */}
        {isForecastTrendLoading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((index) => (
              <Grid item xs={12} sm={6} md={6} lg={6} key={index}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={300} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {forecastTrendData?.quarters?.map((quarter) => (
              <Grid item xs={12} sm={6} md={6} lg={6} key={quarter.quarter}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <ForecastTrendChart
                        data={quarter.weeklyData}
                        quarterLabel={quarter.quarter}
                        quarter={quarter.quarter}
                        status={quarter.status}
                        target={quarter.target}
                        onDrill={handleForecastTrendDrilldown}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Legend */}
        <Box mt={3}>
          <div className={classes.legend}>
            <div className={classes.legendItem}>
              <div className={classes.legendBox} style={{ backgroundColor: '#70ad47' }} />
              <Typography variant="body2">Won</Typography>
            </div>
            <div className={classes.legendItem}>
              <div className={classes.legendBox} style={{ backgroundColor: '#c5e0b4' }} />
              <Typography variant="body2">Most Likely + Commit</Typography>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendBox}
                style={{ backgroundColor: '#bf9000', height: 3, width: 24 }}
              />
              <Typography variant="body2">Target</Typography>
            </div>
          </div>
        </Box>
      </Paper>

      {/* Paper 2: Qualified Pipeline Coverage */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              {qualifiedPipelineCoverageData?.title || 'Qualified Pipeline Coverage'}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isQualifiedPipelineCoverageLoading ? (
            <Skeleton variant="rect" animation="wave" height={350} width="100%" />
          ) : qualifiedPipelineCoverageData?.chartData?.length > 0 ? (
            <QualifiedPipelineCoverageChart
              data={qualifiedPipelineCoverageData.chartData}
              title={qualifiedPipelineCoverageData.title}
              onDrill={handleQualifiedPipelineDrilldown}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <Typography variant="body2" color="textSecondary">
                No data available
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Paper 3: Sales Forecast Current Quarter */}
      <SalesForecastCurrentQuarterTable
        classes={classes}
        data={salesForecastCurrentQuarterData}
        isLoading={isSalesForecastCurrentQuarterLoading}
        asOfDateLabel={asOfDateLabel}
        handleChangeLocation={handleChangeLocation}
      />

      {/* Paper 4: Forecast Moved Out Opps */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              Sales Forecast – Current Quarter – Forecast – Most Likely + Commit Opps moved out in the last week 
            </Typography>
            <span className={classes.updateFrequency}>
              {"Live Data"}
            </span>
          </Box>

          {isForecastMovedOutLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {(!forecastMovedOutData?.movedOutOpps || forecastMovedOutData.movedOutOpps.length === 0) ? (
                <Box textAlign="center" py={4} style={{ fontStyle: 'italic', color: '#666' }}>
                  No forecast opps moved out this week
                </Box>
              ) : (
                <ResizableTable
                  columns={forecastMovedOutColumns}
                  rows={forecastMovedOutRows}
                  getCellValue={getCellValue}
                  getCellStyle={getCellStyle}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'movedOut')}
                />
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* Paper 5: Sales Forecast Next Quarter */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              Sales Forecast – Next Quarter{salesForecastNextQuarterData?.targetQuarter ? ` (${salesForecastNextQuarterData.targetQuarter})` : ''}
            </Typography>
            <span className={classes.updateFrequency}>
              {"Live Data"}
            </span>
          </Box>

          {isSalesForecastNextQuarterLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {/* Summary */}
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Summary
                </Typography>
                <ResizableTable
                  columns={createSummaryTableColumns()}
                  rows={[
                    {
                      label: 'Won QTD',
                      count: salesForecastNextQuarterData?.summary?.won?.count || 0,
                      acvInMillions: salesForecastNextQuarterData?.summary?.won?.acvInMillions || '0.00',
                      rowType: 'won',
                    },
                    {
                      label: 'Most Likely + Commit',
                      count: salesForecastNextQuarterData?.summary?.forecast?.count || 0,
                      acvInMillions: salesForecastNextQuarterData?.summary?.forecast?.acvInMillions || '0.00',
                      rowType: 'forecast',
                    },
                    {
                      label: 'Total Forecast',
                      count: salesForecastNextQuarterData?.summary?.total?.count || 0,
                      acvInMillions: salesForecastNextQuarterData?.summary?.total?.acvInMillions || '0.00',
                      rowType: 'total',
                    },
                    {
                      label: 'Forecast Goal (1.5x Quota)',
                      count: '',
                      acvInMillions: salesForecastNextQuarterData?.summary?.forecastGoal || '0.00',
                      rowType: 'forecast',
                    },
                    {
                      label: 'Quota',
                      count: '',
                      acvInMillions: salesForecastNextQuarterData?.summary?.quotaInMillions || '0.00',
                      rowType: 'quota',
                    },
                  ]}
                  getCellValue={(row, field) => {
                    if (field === 'label') {
                      return row[field];
                    }
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={(row, field) => {
                    const baseStyle = {};
                    if (field === 'label') {
                      baseStyle.fontWeight = 600;
                    }
                    if (row.rowType === 'total') {
                      baseStyle.backgroundColor = '#f5f5f5';
                      if (field === 'count' || field === 'acvInMillions') {
                        baseStyle.fontWeight = 600;
                      }
                    }
                    if (row.rowType === 'quota') {
                      baseStyle.backgroundColor = '#fff9c4';
                      if (field === 'acvInMillions') {
                        baseStyle.fontWeight = 600;
                      }
                    }
                    return baseStyle;
                  }}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="34%"
                />
              </Box>

              {/* Forecast Opps Table */}
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                  Sales Forecast – Next Quarter{salesForecastNextQuarterData?.targetQuarter ? ` (${salesForecastNextQuarterData.targetQuarter})` : ''} – Opps in Forecast – Most Likely + Commit
                </Typography>
                {(!salesForecastNextQuarterData?.forecastOpps || salesForecastNextQuarterData.forecastOpps.length === 0) ? (
                  <Box textAlign="center" py={4} style={{ fontStyle: 'italic', color: '#666' }}>
                    No forecast opportunities for next quarter
                  </Box>
                ) : (
                  <ResizableTable
                    columns={salesForecastNextQuarterColumns}
                    rows={salesForecastNextQuarterRows}
                    getCellValue={getCellValue}
                    getCellStyle={(row, field) => {
                      if (row.label === 'Total' || row.label === 'Quota') {
                        return { fontWeight: 600, backgroundColor: row.label === 'Quota' ? '#fff9c4' : '#f5f5f5' };
                      }
                      if (row.isNewThisWeek) {
                        return { backgroundColor: '#c8e6c9' };
                      }
                      return {};
                    }}
                    rowKey="label"
                    hideSubHeaderRow={true}
                    maxWidth="100%"
                    onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'nextQuarter')}
                  />
                )}
              </Box>

              {/* Legend for green highlighting */}
              <Box mt={2}>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                    <Typography variant="body2">New this week</Typography>
                  </div>
                </div>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Paper 6: Sales Forecast Next Quarter + 1 */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              Sales Forecast – Next Quarter +1{salesForecastNextQuarterPlus1Data?.targetQuarter ? ` (${salesForecastNextQuarterPlus1Data.targetQuarter})` : ''}
            </Typography>
            <span className={classes.updateFrequency}>
              {"Live Data"}
            </span>
          </Box>

          {isSalesForecastNextQuarterPlus1Loading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {/* Summary */}
              <Box mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: 600}}>
                  Summary
                </Typography>
                <ResizableTable
                  columns={createSummaryTableColumns()}
                  rows={[
                    {
                      label: 'Won QTD',
                      count: salesForecastNextQuarterPlus1Data?.summary?.won?.count || 0,
                      acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.won?.acvInMillions || '0.00',
                      rowType: 'won',
                    },
                    {
                      label: 'Most Likely + Commit',
                      count: salesForecastNextQuarterPlus1Data?.summary?.forecast?.count || 0,
                      acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.forecast?.acvInMillions || '0.00',
                      rowType: 'forecast',
                    },
                    {
                      label: 'Total Forecast',
                      count: salesForecastNextQuarterPlus1Data?.summary?.total?.count || 0,
                      acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.total?.acvInMillions || '0.00',
                      rowType: 'total',
                    },
                    {
                      label: 'Forecast Goal (1.5x Quota)',
                      count: '',
                      acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.forecastGoal || '0.00',
                      rowType: 'forecast',
                    },
                    {
                      label: 'Quota',
                      count: '',
                      acvInMillions: salesForecastNextQuarterPlus1Data?.summary?.quotaInMillions || '0.00',
                      rowType: 'quota',
                    },
                  ]}
                  getCellValue={(row, field) => {
                    if (field === 'label') {
                      return row[field];
                    }
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={(row, field) => {
                    const baseStyle = {};
                    if (field === 'label') {
                      baseStyle.fontWeight = 600;
                    }
                    if (row.rowType === 'total') {
                      baseStyle.backgroundColor = '#f5f5f5';
                      if (field === 'count' || field === 'acvInMillions') {
                        baseStyle.fontWeight = 600;
                      }
                    }
                    if (row.rowType === 'quota') {
                      baseStyle.backgroundColor = '#fff9c4';
                      if (field === 'acvInMillions') {
                        baseStyle.fontWeight = 600;
                      }
                    }
                    return baseStyle;
                  }}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="34%"
                />
              </Box>

              {/* Forecast Opps Table */}
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                  Sales Forecast – Next Quarter +1{salesForecastNextQuarterPlus1Data?.targetQuarter ? ` (${salesForecastNextQuarterPlus1Data.targetQuarter})` : ''} – Opps in Forecast – Most Likely + Commit
                </Typography>
                {(!salesForecastNextQuarterPlus1Data?.forecastOpps || salesForecastNextQuarterPlus1Data.forecastOpps.length === 0) ? (
                  <Box textAlign="center" py={4} style={{ fontStyle: 'italic', color: '#666' }}>
                    No forecast opportunities for next quarter +1
                  </Box>
                ) : (
                  <ResizableTable
                    columns={salesForecastNextQuarterPlus1Columns}
                    rows={salesForecastNextQuarterPlus1Rows}
                    getCellValue={getCellValue}
                    getCellStyle={(row, field) => {
                      if (row.label === 'Total' || row.label === 'Quota') {
                        return { fontWeight: 600, backgroundColor: row.label === 'Quota' ? '#fff9c4' : '#f5f5f5' };
                      }
                      if (row.isNewThisWeek) {
                        return { backgroundColor: '#c8e6c9' };
                      }
                      return {};
                    }}
                    rowKey="label"
                    hideSubHeaderRow={true}
                    maxWidth="100%"
                    onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'nextQuarterPlus1')}
                  />
                )}
              </Box>

              {/* Legend for green highlighting */}
              <Box mt={2}>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                    <Typography variant="body2">New this week</Typography>
                  </div>
                </div>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Deal Progression and Closures */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              {/* Deal Progression and Closures */}
              {dealProgressionData?.title}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isDealProgressionLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {/* Chart 1: Cumulative Closed Won by Month */}
              <Grid item xs={12} md={12}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <CumulativeClosedWonChart
                        data={dealProgressionData?.cumulativeClosedWon}
                        fiscalYear={dealProgressionData?.fiscalYear}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 2: Pipeline percent by stage */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <PipelineByStageChart
                        data={dealProgressionData?.pipelineByStage}
                        currentFiscalQuarter={dealProgressionData?.currentFiscalQuarter}
                        stageCategories={dealProgressionData?.stageCategories}
                      />
                    </div>
                    {/* Legend for stacked bar - Dynamic */}
                    <Box mt={2}>
                      <div className={classes.legend}>
                        {(() => {
                          const colorScale = d3.scaleOrdinal(updatedScheme10);
                          return dealProgressionData?.stageCategories?.map((category, index) => (
                            <div key={category} className={classes.legendItem}>
                              <div
                                className={classes.legendBox}
                                style={{ backgroundColor: colorScale(category) }}
                              />
                              <Typography variant="body2">{category}</Typography>
                            </div>
                          ));
                        })()}
                      </div>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 3: Forecast Accuracy */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <ForecastAccuracyChart
                        data={dealProgressionData?.forecastAccuracy}
                        target={dealProgressionData?.forecastAccuracyTarget}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Box>

      {/* Paper 8: Deal Conversion Efficiency */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              {/* Deal Conversion Efficiency */}
              {dealConversionData?.title}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isDealConversionLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {/* Chart 1: Pipeline Aging */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <PipelineAgingChart
                        data={dealConversionData?.pipelineAging}
                        ageBuckets={dealConversionData?.ageBuckets}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 2: Conversion Cycletime */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <ConversionCycletimeChart
                        data={dealConversionData?.conversionCycletime}
                        timeBuckets={dealConversionData?.timeBuckets}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 3: Percent Conversion from Qualification */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <QualificationConversionChart
                        data={dealConversionData?.qualificationConversion}
                        target={dealConversionData?.qualificationTarget}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 4: Percent Conversion from POC */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <POCConversionChart
                        data={dealConversionData?.pocConversion}
                        target={dealConversionData?.pocTarget}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Box>

      {/* Win-Loss Analysis */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Win-Loss analysis: Month of XXX
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          <Grid container spacing={2}>
            {/* Won Opportunities Table */}
            <Grid item xs={12} md={6}>
              <ResizableTable
                columns={createWinLossTableColumns()}
                rows={[
                  { customerName: 'Customer A', acvInMillions: 1.2, competition: 'Competitor X', keyLearnings: 'Strong technical fit', label: 'Customer A' },
                  { customerName: 'Customer B', acvInMillions: 0.8, competition: 'None', keyLearnings: 'Existing relationship', label: 'Customer B' },
                ]}
                getCellValue={(row, field) => row[field] || ''}
                rowKey="label"
                heading="Won Opportunities"
                hideSubHeaderRow={true}
                maxWidth="100%"
                onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'winLossWon')}
              />
            </Grid>

            {/* Lost Opportunities Table */}
            <Grid item xs={12} md={6}>
              <ResizableTable
                columns={createWinLossTableColumns()}
                rows={[
                  { customerName: 'Customer C', acvInMillions: 1.5, competition: 'Competitor Y', keyLearnings: 'Price sensitivity', label: 'Customer C' },
                  { customerName: 'Customer D', acvInMillions: 0.9, competition: 'Competitor Z', keyLearnings: 'Feature gaps', label: 'Customer D' },
                ]}
                getCellValue={(row, field) => row[field] || ''}
                rowKey="label"
                heading="Lost Opportunities"
                hideSubHeaderRow={true}
                maxWidth="100%"
                onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'winLossLost')}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 9: MSSP Fulfillment */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              MSSP Fulfillment – Cumulative ACV ($M)
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Box className={classes.chartContainer} style={{ minHeight: 350 }}>
                    <Skeleton variant="rect" animation="wave" height={300} width="100%" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 10: Renewals Tracker Summary */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Renewals tracker summary
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          <Grid container spacing={3}>
            {/* Monthly Renewals Chart */}
            <Grid item xs={12} md={6}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Monthly Renewals ($M)
                  </Typography>
                  <Box className={classes.chartContainer}>
                    <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Cumulative Renewals Chart */}
            <Grid item xs={12} md={6}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cumulative Renewals ($M)
                  </Typography>
                  <Box className={classes.chartContainer}>
                    <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 11: Renewals Tracker Detail */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Renewals tracker detail – Month of XXX
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ResizableTable
                columns={createRenewalsTableColumns()}
                rows={[
                  { rowNum: 1, customerName: 'Customer Alpha', renewalDate: '2026-03-15', contractValue: 2.5, probability: 90, status: 'On Track', riskFactors: 'None', actionItems: 'Schedule QBR', label: 'Customer Alpha' },
                  { rowNum: 2, customerName: 'Customer Beta', renewalDate: '2026-03-20', contractValue: 1.8, probability: 75, status: 'At Risk', riskFactors: 'Budget concerns', actionItems: 'Executive engagement needed', label: 'Customer Beta' },
                  { rowNum: 3, customerName: 'Customer Gamma', renewalDate: '2026-03-25', contractValue: 3.2, probability: 95, status: 'Committed', riskFactors: 'None', actionItems: 'Send contract', label: 'Customer Gamma' },
                  { rowNum: null, customerName: 'TOTAL', renewalDate: null, contractValue: 7.5, probability: null, status: null, riskFactors: null, actionItems: null, label: 'Total' },
                ]}
                  getCellValue={(row, field, rowIndex) => {
                    // Always show sequential row numbers regardless of sort order
                    if (field === 'rowNum') {
                      // For Total rows, return null
                      if (row.label === 'Total') {
                        return null;
                      }
                      // For regular rows, use rowIndex to show sequential numbers
                      return rowIndex != null ? rowIndex + 1 : (row[field] != null ? row[field] : '');
                    }
                    if (row.label === 'Total' && field !== 'customerName' && field !== 'contractValue') {
                      return '';
                    }
                    return row[field] != null ? row[field] : '';
                  }}
                getCellStyle={(row, field) => {
                  if (row.label === 'Total') {
                    return { fontWeight: 600, backgroundColor: '#f5f5f5' };
                  }
                  return {};
                }}
                rowKey="label"
                hideSubHeaderRow={true}
                maxWidth="100%"
                onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'renewals')}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 12: Upsell-Cross Sell Tracker */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              {/* Upsell-Cross Sell Tracker */}
              {upsellCrossSellData?.title}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isUpsellCrossSellLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {(() => {
                // When "All" is selected, show Company data; otherwise show selected team's data
                const teamKey = appliedTeam && appliedTeam !== 'All' ? appliedTeam : 'Company';
                const teamData = upsellCrossSellData?.teams?.[teamKey];
                const displayName = teamKey === 'Company' ? 'ColorTokens' : teamKey;

                if (!teamData) return null;

                return (
                  <Grid item xs={12} >
                    <Card className={classes.chartCard}>
                      <CardContent>
                        <div className={classes.chartContainer} id="printChart">
                          <UpsellCrossSellChart
                            data={teamData}
                            teamName={displayName}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })()}
            </Grid>
          )}

          {/* Legend for cumulative target vs actual */}
          <Box mt={2}>
            <div className={classes.legend}>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{ backgroundColor: '#bf9000' }}
                />
                <Typography variant="body2">Cumulative (target)</Typography>
              </div>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{ backgroundColor: '#70ad47' }}
                />
                <Typography variant="body2">Cumulative (actual)</Typography>
              </div>
            </div>
          </Box>
        </Paper>
      </Box>

      {/* Paper 13: Upsell-Cross Sell Tracker Detail */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Upsell-Cross Sell tracker detail
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ResizableTable
                columns={createUpsellCrossSellDetailColumns(['February', 'March', 'April', 'May', 'June', 'July'])}
                rows={[
                  ...Array.from({ length: 6 }, (_, i) => ({
                    rowLabel: '',
                    February_customer: '',
                    February_target: '',
                    February_actual: '',
                    March_customer: '',
                    March_target: '',
                    March_actual: '',
                    April_customer: '',
                    April_target: '',
                    April_actual: '',
                    May_customer: '',
                    May_target: '',
                    May_actual: '',
                    June_customer: '',
                    June_target: '',
                    June_actual: '',
                    July_customer: '',
                    July_target: '',
                    July_actual: '',
                    label: `row_${i + 1}`,
                  })),
                  {
                    rowLabel: 'TOTAL =',
                    February_customer: '',
                    February_target: '',
                    February_actual: '',
                    March_customer: '',
                    March_target: '',
                    March_actual: '',
                    April_customer: '',
                    April_target: '',
                    April_actual: '',
                    May_customer: '',
                    May_target: '',
                    May_actual: '',
                    June_customer: '',
                    June_target: '',
                    June_actual: '',
                    July_customer: '',
                    July_target: '',
                    July_actual: '',
                    label: 'Total',
                  },
                ]}
                getCellValue={(row, field) => row[field] || ''}
                getCellStyle={(row, field) => {
                  if (row.label === 'Total') {
                    return { fontWeight: 600, backgroundColor: '#f5f5f5' };
                  }
                  return {};
                }}
                rowKey="label"
                stickyFirstColumn={true}
                maxWidth="100%"
                onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'upsellCrossSell')}
              />
            </Grid>
          </Grid>

          <Box mt={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ResizableTable
                  columns={createUpsellCrossSellDetailColumns(['August', 'September', 'October', 'November', 'December', 'January'])}
                  rows={[
                    ...Array.from({ length: 6 }, (_, i) => ({
                      rowLabel: '',
                      August_customer: '',
                      August_target: '',
                      August_actual: '',
                      September_customer: '',
                      September_target: '',
                      September_actual: '',
                      October_customer: '',
                      October_target: '',
                      October_actual: '',
                      November_customer: '',
                      November_target: '',
                      November_actual: '',
                      December_customer: '',
                      December_target: '',
                      December_actual: '',
                      January_customer: '',
                      January_target: '',
                      January_actual: '',
                      label: `row_${i + 1}`,
                    })),
                    {
                      rowLabel: 'TOTAL =',
                      August_customer: '',
                      August_target: '',
                      August_actual: '',
                      September_customer: '',
                      September_target: '',
                      September_actual: '',
                      October_customer: '',
                      October_target: '',
                      October_actual: '',
                      November_customer: '',
                      November_target: '',
                      November_actual: '',
                      December_customer: '',
                      December_target: '',
                      December_actual: '',
                      January_customer: '',
                      January_target: '',
                      January_actual: '',
                      label: 'Total',
                    },
                  ]}
                  getCellValue={(row, field) => row[field] || ''}
                  getCellStyle={(row, field) => {
                    if (row.label === 'Total') {
                      return { fontWeight: 600, backgroundColor: '#f5f5f5' };
                    }
                    return {};
                  }}
                  rowKey="label"
                  stickyFirstColumn={true}
                  maxWidth="100%"
                  onRowClick={(row) => handleChangeLocation("drilldownOnGraph", row, 'upsellCrossSell')}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>

      {/* Forecast Trend Drilldown - First Level */}
      {(forecastTrendDrilldownLocation === 'won' || forecastTrendDrilldownLocation === 'mostLikely') && secondLevelLocation !== 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={forecastTrendDrilldownLocation === 'won'
              ? 'Won Opportunities'
              : 'Most Likely / Commit Opportunities'}
            back={handleForecastTrendDrilldownBack}
            filters={forecastTrendDrilldownFilters}
          >
            <DrilldownWithSLD
              header={forecastTrendDrilldownLocation === 'won'
                ? 'Closed-Won Opportunities'
                : 'Pipeline Opportunities (Most Likely / Commit)'}
              body={props.forecastTrendDrilldownData}
              company={props.company}
              Value_Label={props.Value_Label}
              handleSecondLevelDrilldown={handleSecondLevelDrilldown}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* Forecast Trend Drilldown - Second Level (Opportunity Change Events) */}
      {(forecastTrendDrilldownLocation === 'won' || forecastTrendDrilldownLocation === 'mostLikely') && secondLevelLocation === 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleForecastTrendDrilldownBack}
            stepBack={handleForecastTrendStepBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* Qualified Pipeline Coverage Drilldown - First Level */}
      {qualifiedPipelineDrilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={'Qualified Pipeline Opportunities'}
            back={handleQualifiedPipelineDrilldownBack}
            filters={qualifiedPipelineDrilldownFilters}
          >
            <DrilldownWithSLD
              header={'Open Qualified Opportunities'}
              body={props.qualifiedPipelineCoverageDrilldownData}
              company={props.company}
              Value_Label={props.Value_Label}
              handleSecondLevelDrilldown={handleSecondLevelDrilldown}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* Qualified Pipeline Coverage Drilldown - Second Level (Opportunity Change Events) */}
      {qualifiedPipelineDrilldownOpen && secondLevelLocation === 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleQualifiedPipelineDrilldownBack}
            stepBack={handleQualifiedPipelineStepBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* Second Level Drilldown (for other charts) */}
      {secondLevelLocation === "drilldownOppId" && !forecastTrendDrilldownLocation && !qualifiedPipelineDrilldownOpen && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleChangeLocationBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, forecastTrendData, forecastTrendDrilldownData, salesForecastCurrentQuarterData, forecastMovedOutData, salesForecastNextQuarterData, salesForecastNextQuarterPlus1Data, dealProgressionData, dealConversionData, upsellCrossSellData, qualifiedPipelineCoverageData, qualifiedPipelineCoverageDrilldownData, rsrFirstLevelTeams, rsrSelectedTeam, rsrDefaultTeam, ExpectedAcvData } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    forecastTrendData,
    forecastTrendDrilldownData,
    salesForecastCurrentQuarterData,
    forecastMovedOutData,
    salesForecastNextQuarterData,
    salesForecastNextQuarterPlus1Data,
    dealProgressionData,
    dealConversionData,
    upsellCrossSellData,
    qualifiedPipelineCoverageData,
    qualifiedPipelineCoverageDrilldownData,
    rsrFirstLevelTeams,
    rsrSelectedTeam,
    rsrDefaultTeam,
    ExpectedAcvData,
    helperTextCharts: user.elements.flatMap((item) => item.charts || []),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFirstLevelTeams: () => {
    dispatch({ type: "get_rsr_first_level_teams_request" });
    appService.getRegionalSalesReviewFirstLevelTeams().then(
      (json) => {
        dispatch({ type: "get_rsr_first_level_teams_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsr_first_level_teams_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsr_first_level_teams_failure", error });
      }
    );
  },
  getForecastTrendData: (filters = []) => {
    dispatch({ type: "get_forecast_trend_data_request" });
    appService.getRegionalSalesReviewForecastTrend(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_data_failure", error });
      }
    );
  },
  getSalesForecastCurrentQuarterData: (filters = []) => {
    dispatch({ type: "get_sales_forecast_current_quarter_data_request" });
    appService.getRegionalSalesReviewSalesForecastCurrentQuarter(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_current_quarter_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_current_quarter_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_current_quarter_data_failure", error });
      }
    );
  },
  getDealProgressionData: (filters = []) => {
    dispatch({ type: "get_deal_progression_data_request" });
    appService.getRegionalSalesReviewDealProgression(filters).then(
      (json) => {
        dispatch({ type: "get_deal_progression_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_progression_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_progression_data_failure", error });
      }
    );
  },
  getDealConversionData: (filters = []) => {
    dispatch({ type: "get_deal_conversion_data_request" });
    appService.getRegionalSalesReviewDealConversion(filters).then(
      (json) => {
        dispatch({ type: "get_deal_conversion_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_conversion_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_conversion_data_failure", error });
      }
    );
  },
  getUpsellCrossSellData: (filters = []) => {
    dispatch({ type: "get_upsell_cross_sell_data_request" });
    appService.getRegionalSalesReviewUpsellCrossSell(filters).then(
      (json) => {
        dispatch({ type: "get_upsell_cross_sell_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_upsell_cross_sell_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_upsell_cross_sell_data_failure", error });
      }
    );
  },
  getForecastMovedOutData: (filters = []) => {
    dispatch({ type: "get_forecast_moved_out_data_request" });
    appService.getRegionalSalesReviewForecastMovedOut(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_moved_out_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_moved_out_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_moved_out_data_failure", error });
      }
    );
  },
  getSalesForecastNextQuarterData: (filters = []) => {
    dispatch({ type: "get_sales_forecast_next_quarter_data_request" });
    appService.getRegionalSalesReviewSalesForecastNextQuarter(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_next_quarter_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_next_quarter_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_next_quarter_data_failure", error });
      }
    );
  },
  getSalesForecastNextQuarterPlus1Data: (filters = []) => {
    dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_request" });
    appService.getRegionalSalesReviewSalesForecastNextQuarterPlus1(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_next_quarter_plus1_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_failure", error });
      }
    );
  },
  getQualifiedPipelineCoverageData: (filters = []) => {
    dispatch({ type: "get_qualified_pipeline_coverage_data_request" });
    appService.getPipelineReviewQualifiedPipelineCoverage(filters).then(
      (json) => {
        dispatch({ type: "get_qualified_pipeline_coverage_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_qualified_pipeline_coverage_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_qualified_pipeline_coverage_data_failure", error });
      }
    );
  },
  getOpenOppData: (oppId) => {
    dispatch({ type: "get_expectedAcv_drilldown_data_request" });
    appService.getWaterfallOppData(oppId).then(
      (json) => {
        dispatch({ type: "get_expectedAcv_drilldown_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_expectedAcv_drilldown_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_expectedAcv_drilldown_data_failure", error });
      }
    );
  },
  clearExpectedAcvData: () => {
    dispatch({ type: "clear_expected_acv_data" });
  },
  getForecastTrendWonDrilldown: (filters = []) => {
    dispatch({ type: "get_forecast_trend_drilldown_request" });
    appService.getRegionalSalesReviewForecastTrendWonDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_drilldown_failure", error });
      }
    );
  },
  getForecastTrendMostLikelyDrilldown: (filters = []) => {
    dispatch({ type: "get_forecast_trend_drilldown_request" });
    appService.getRegionalSalesReviewForecastTrendMostLikelyDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_drilldown_failure", error });
      }
    );
  },
  clearForecastTrendDrilldown: () => {
    dispatch({ type: "clear_forecast_trend_drilldown" });
  },
  getQualifiedPipelineCoverageDrilldown: (filters = []) => {
    dispatch({ type: "get_qualified_pipeline_coverage_drilldown_request" });
    appService.getPipelineReviewQualifiedPipelineCoverageDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_qualified_pipeline_coverage_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_qualified_pipeline_coverage_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_qualified_pipeline_coverage_drilldown_failure", error });
      }
    );
  },
  clearQualifiedPipelineCoverageDrilldown: () => {
    dispatch({ type: "clear_qualified_pipeline_coverage_drilldown" });
  },
});

const connectedRegionalSalesReviewPackage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RegionalSalesReviewPackage));

export default connectedRegionalSalesReviewPackage;